// Libraries
import React from 'react';

// Supermove
import {useSupportTools} from '@supermove/analytics';
import {Icon, IconSource, Link, Popover, Space, Styled, SupermoveLogo} from '@supermove/components';
import {useModal, usePopover, useResponsive, useRef, useEffect} from '@supermove/hooks';
import {UserModel} from '@supermove/models';
import {colors, Typography} from '@supermove/styles';

// App
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import EnvironmentBadge from '@shared/modules/App/components/EnvironmentBadge';
import HelpCenterButton from '@shared/modules/App/components/HelpCenterButton';
import UpdateSuperAdminOrganizationModal from '@shared/modules/App/components/UpdateSuperAdminOrganizationModal';
import SupermoveProductKind from '@shared/modules/SupermoveProduct/enums/SupermoveProductKind';
import UserRole from '@shared/modules/User/enums/UserRole';
import GlobalHeaderMultibranchOrganizationSwitcherModal from 'modules/App/Global/GlobalHeaderMultibranchOrganizationSwitcherModal';
import GlobalCurrentUserActions from 'modules/App/Global/components/GlobalCurrentUserActions';
import GlobalSearchModal from 'modules/App/Global/components/GlobalSearchModal';
import useUserNotificationsCount from 'modules/App/Global/hooks/useUserNotificationsCount';
import ManagerSearch from 'modules/App/components/ManagerSearch';
import NotificationPopover from 'modules/App/components/OfficeHeader/NotificationPopover';
import useAppContext from 'modules/App/context/useAppContext';

const HeaderContainer = Styled.View`
  flex-direction: row;
  height: 48px;
  background-color: ${colors.black};
  align-items: center;
`;

const LogoContainer = Styled.View`
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 3px;
`;

const OrganizationNameText = Styled.Text`
  ${Typography.Label}
  color: ${colors.white};
`;

const AlertDot = Styled.View`
  height: 8px;
  width: 8px;
  border-radius: 4px;
  background-color: ${colors.red.warning};
  position: absolute;
  top: -4px;
  right: -6px;
`;

const HeaderIconButton = ({
  icon,
  onPress,
  hasAlert,
  onLongPress,
}: {
  icon: IconSource;
  onPress: () => void;
  hasAlert?: boolean;
  onLongPress?: () => void;
}) => {
  return (
    <TertiaryButton
      onPress={onPress}
      onLongPress={onLongPress}
      style={{
        paddingHorizontal: 8,
        paddingVertical: 8,
      }}
    >
      <Icon source={icon} size={16} color={colors.white} />
      {hasAlert && <AlertDot />}
    </TertiaryButton>
  );
};

const ToggleSidebarButton = () => {
  const {toggleIsGlobalNavSidebarExpanded} = useAppContext();

  return <HeaderIconButton icon={Icon.Bars} onPress={toggleIsGlobalNavSidebarExpanded} />;
};

const LogoButton = () => {
  return (
    <Link to={'/'}>
      <LogoContainer>
        <SupermoveLogo.Icon />
      </LogoContainer>
    </Link>
  );
};

const OrganizationName = () => {
  const responsive = useResponsive();
  const {viewer} = useAppContext();
  const multibranchOrganizationSwitcher = useModal({
    name: 'Multibranch Organization Switcher',
    enableTracking: true,
  });
  const isSuperAdminViewingMultiBranch =
    viewer?.role === UserRole.SUPER_ADMIN && viewer?.viewingOrganization.hasMultipleOrganizations;
  return (
    <React.Fragment>
      {(isSuperAdminViewingMultiBranch || viewer?.isPrimaryOrganizationAdmin) &&
      viewer?.viewingOrganization.features.isEnabledMultibranchOrganizationSwitcher ? (
        <TertiaryButton
          text={viewer?.viewingOrganization.name}
          textColor={colors.white}
          iconRight={Icon.ChevronDown}
          onPress={multibranchOrganizationSwitcher.handleOpen}
          isLarge={responsive.mobile}
        />
      ) : (
        <OrganizationNameText>{viewer?.viewingOrganization.name}</OrganizationNameText>
      )}
      <GlobalHeaderMultibranchOrganizationSwitcherModal
        key={multibranchOrganizationSwitcher.key}
        isOpen={multibranchOrganizationSwitcher.isOpen}
        handleClose={multibranchOrganizationSwitcher.handleClose}
      />
    </React.Fragment>
  );
};

const SearchButton = ({
  handleOpenSearch,
  handleOpenGlobalSearchV2,
  isSuperAdmin = false,
}: {
  handleOpenSearch: () => void;
  handleOpenGlobalSearchV2?: () => void;
  isSuperAdmin?: boolean;
}) => {
  const {viewer} = useAppContext();
  const keyPressHandlerRef = useRef(null);

  useEffect(() => {
    const handleKeyPress = (event: any) => {
      if ((event.metaKey || event.ctrlKey) && (event.key === 'k' || event.key === 'K')) {
        // @ts-expect-error TS(2349): This expression is not callable.
        keyPressHandlerRef.current && keyPressHandlerRef.current();
        handleOpenSearch && handleOpenSearch();
      }
    };

    // since we're looking for a combined keypress (cmd/ctrl + k), we need to listen to the keydown event
    window.addEventListener('keydown', handleKeyPress);
    // clean up the listener when the component is unmounted
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  return (
    <React.Fragment>
      {handleOpenSearch ? (
        <HeaderIconButton icon={Icon.Search} onPress={handleOpenSearch} />
      ) : (
        <ManagerSearch
          organization={viewer?.viewingOrganization}
          trigger={({handleOpen}: any) => {
            // HACK: we need to store the handleOpen function in a ref so we can call it from the keypress event listener
            if (!keyPressHandlerRef.current) {
              keyPressHandlerRef.current = handleOpen;
            }
            return (
              <HeaderIconButton
                icon={Icon.Search}
                onPress={handleOpen}
                onLongPress={isSuperAdmin ? handleOpenGlobalSearchV2 : undefined}
              />
            );
          }}
        />
      )}
    </React.Fragment>
  );
};

const NotificationsButton = ({handleOpenNotifications, hasUnseenNotifications}: any) => {
  return (
    <HeaderIconButton
      icon={Icon.Bell}
      onPress={handleOpenNotifications}
      hasAlert={hasUnseenNotifications}
    />
  );
};

const HelpButton = () => {
  const {handleOpen} = useSupportTools({isChatVisible: false});

  return <HeaderIconButton icon={Icon.CommentQuestion} onPress={handleOpen} />;
};

const SettingsButton = () => {
  return (
    <Link to={'/settings'} style={{padding: 8}}>
      <Icon source={Icon.Gear} size={16} color={colors.white} />
    </Link>
  );
};

const SuperAdminOrganizationSwitcher = () => {
  const {viewer} = useAppContext();
  const updateSuperAdminOrganizationModal = useModal({
    name: 'Update Super Admin Organization Modal',
  });
  return (
    <React.Fragment>
      <HeaderIconButton icon={Icon.Ghost} onPress={updateSuperAdminOrganizationModal.handleOpen} />
      <UpdateSuperAdminOrganizationModal
        key={updateSuperAdminOrganizationModal.key}
        isOpen={updateSuperAdminOrganizationModal.isOpen}
        handleClose={updateSuperAdminOrganizationModal.handleClose}
        // NOTE(jholston): This is a hack since useAppContext returns AppContextProviderViewerType instead of UserModel
        viewer={viewer as any as UserModel}
        supermoveProductKind={SupermoveProductKind.OFFICE}
      />
      <Space width={16} />
    </React.Fragment>
  );
};

const GlobalHeaderContent = ({
  globalSearchModal,
  notificationPopover,
  hasUnseenNotifications,
}: any) => {
  const {viewer} = useAppContext();
  const {
    isEnabledGlobalSearchV2 = false,
    isEnabledGlobalHelpDropdown = false,
    isEnabledHelpDropdownChatOption = false,
  } = viewer?.viewingOrganization.features || {};

  return (
    <React.Fragment>
      <Space width={16} />
      <ToggleSidebarButton />
      <Space width={16} />
      <LogoButton />
      <Space width={4} />
      <OrganizationName />
      <EnvironmentBadge />
      <Space style={{flex: 1}} />
      <SearchButton
        handleOpenSearch={isEnabledGlobalSearchV2 && globalSearchModal.handleOpen}
        handleOpenGlobalSearchV2={globalSearchModal.handleOpen}
        isSuperAdmin={viewer?.role === UserRole.SUPER_ADMIN}
      />
      <Space width={16} />
      <Popover.Content innerRef={notificationPopover.ref}>
        <NotificationsButton
          handleOpenNotifications={notificationPopover.handleToggle}
          hasUnseenNotifications={hasUnseenNotifications}
        />
      </Popover.Content>
      <Space width={16} />
      {isEnabledGlobalHelpDropdown ? (
        <HelpCenterButton
          referralUtmSource={'office-app'}
          showChatOption={isEnabledHelpDropdownChatOption}
        />
      ) : (
        <HelpButton />
      )}

      <Space width={16} />
      <SettingsButton />
      <Space width={16} />
      {viewer?.role === UserRole.SUPER_ADMIN && <SuperAdminOrganizationSwitcher />}
      <GlobalCurrentUserActions />
      <Space width={24} />
    </React.Fragment>
  );
};

const GlobalHeader = () => {
  const globalSearchModal = useModal({name: 'Office Global Search Modal', enableTracking: true});
  const notificationPopover = usePopover({
    name: 'Office Header Notifications Popover',
    enableTracking: true,
  });
  const {unseenNotificationsCount, refetch: refetchCount} = useUserNotificationsCount();

  return (
    <HeaderContainer>
      <GlobalHeaderContent
        globalSearchModal={globalSearchModal}
        notificationPopover={notificationPopover}
        hasUnseenNotifications={!!unseenNotificationsCount}
      />
      <GlobalSearchModal
        key={globalSearchModal.key}
        isOpen={globalSearchModal.isOpen}
        handleClose={globalSearchModal.handleClose}
      />
      <NotificationPopover
        isOpen={notificationPopover.isOpen}
        handleOpen={notificationPopover.handleOpen}
        handleClose={notificationPopover.handleClose}
        popoverRef={notificationPopover.ref}
        refetchCount={refetchCount}
      />
    </HeaderContainer>
  );
};

GlobalHeader.OrganizationName = OrganizationName;
GlobalHeader.SearchButton = SearchButton;
GlobalHeader.NotificationsButton = NotificationsButton;
GlobalHeader.SuperAdminOrganizationSwitcher = SuperAdminOrganizationSwitcher;
GlobalHeader.IconButton = HeaderIconButton;

export default GlobalHeader;
